import { ComponentProps } from "react";

import { Button, Modal, Text } from "@vericus/cadmus-ui";

import { create, useModal } from "@ebay/nice-modal-react";

import { useAppDispatch } from "@/data/hooks";

import { goToPage } from "../../answer-slice";
import { useReviewableAnswers } from "../../hooks/use-reviewable-answers";
import { ReviewList } from "../review-list";
import * as styles from "./review-modal.css";

export interface ReviewModalProps {
  workId: string;
  onAgree: () => void;
}

/**
 * Complete data enriched answer Review Modal which acts as a barrier to the
 * Submission Preview page by listing the unanswered questions and prompting the
 * student to agree to continue.
 */
export const ReviewModal = create<ReviewModalProps>(({ onAgree, workId }) => {
  const modal = useModal();
  const dispatch = useAppDispatch();
  const answers = useReviewableAnswers(workId);

  return (
    <ReviewModalInner
      open={modal.visible}
      onExitComplete={modal.remove}
      onCancel={modal.hide}
      onAgree={() => {
        modal.hide();
        onAgree();
      }}
      answers={answers}
      onGoToAnswer={(answerId) => {
        modal.hide();
        dispatch(goToPage({ page: "answer", answerId }));
      }}
    />
  );
});

// Inner modal props for the actual content
interface InnerProps extends ComponentProps<typeof ReviewList> {
  open: boolean;
  onExitComplete?: VoidFunction;
  onCancel: VoidFunction;
  onAgree: VoidFunction;
  onGoToAnswer: (answerBlockId: string) => void;
}

export function ReviewModalInner(props: InnerProps) {
  return (
    <Modal.Root open={props.open}>
      <Modal.Content
        className={styles.modal}
        onExitComplete={props.onExitComplete}
        withCloseButton={false}
      >
        <Modal.Title asChild>
          <Text kind="headingFour">
            You still have {props.answers.length} unanswered{" "}
            {props.answers.length === 1 ? "question" : "questions"}
          </Text>
        </Modal.Title>
        <Modal.Description asChild>
          <Text kind="bodyMd">Are you sure you want to submit?</Text>
        </Modal.Description>
        <ReviewList answers={props.answers} onGoToAnswer={props.onGoToAnswer} />
        <div className={styles.actions}>
          <Button onClick={props.onCancel}>cancel</Button>
          <Button kind="secondary" onClick={props.onAgree}>
            yes, submit
          </Button>
        </div>
      </Modal.Content>
    </Modal.Root>
  );
}
